import type { Ref } from 'react';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { TextInput } from '../form';
import { intl } from '../../lib';

/**
 * Honeypot form state type.
 */
type FormState = {
  firstName: string;
  lastName: string;
  email: string;
};

/**
 * Type of the ref of a {@link SecurityForm}.
 * The ref only contains the state of the form, so this type is implicitly also the form state.
 */
export type SecurityFormRef = FormState;

/**
 * Properties of a {@link SecurityForm} component.
 */
export type SecurityFormProps = {
  onChange?: (state: FormState) => void;
};

/**
 * A Honeypot form, which is meant to help with bot protection.
 *
 * @return the created {@link JSX.Element}.
 */
export const SecurityForm = forwardRef<SecurityFormRef, SecurityFormProps>(
  function (
    { onChange, ...props }: Readonly<SecurityFormProps>,
    ref: Ref<SecurityFormRef>
  ): JSX.Element {
    const [state, setState] = useState<FormState>({
      firstName: '',
      lastName: '',
      email: ''
    });

    const stateChange = (changes: Partial<FormState>) => {
      const newState = { ...state, ...changes };
      setState(newState);
      onChange?.(newState);
    };

    // imperative handle to put the form state into the forwarded ref. To make accessing the state directly without using `onChange` possible.
    useImperativeHandle<Record<string, unknown>, FormState>(ref, () => state, [
      state
    ]);

    return (
      <form
        aria-label={intl('common.form.onlyforbots')} // text for screenreaders, so that a human can understand that this is for bots
        className="ehs-security-form"
        {...props}
      >
        <TextInput
          className="security-form-form-element"
          type="text"
          label="First Name"
          required={true}
          placeholder="Please enter a name:"
          value={state.firstName ?? ''}
          onChange={(value): void => {
            stateChange({
              firstName: value
            });
          }}
        />
        <TextInput
          className="security-form-form-element"
          type="text"
          label="Last Name"
          placeholder="Please enter a name:"
          value={state.lastName ?? ''}
          required={true}
          onChange={(value): void => {
            stateChange({
              lastName: value
            });
          }}
        />
        <TextInput
          className="security-form-form-element"
          type="text"
          label="Email"
          placeholder="Please enter email:"
          value={state.email ?? ''}
          required={true}
          onChange={(value): void => {
            stateChange({
              email: value
            });
          }}
        />
      </form>
    );
  }
);
